import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import * as s from "../InputPage.styles";
import * as t from "./tractor.styles";
import { Slider } from "@mui/material";
import TractorPickerModal from "./TractorPickerModal";
import { AnalysisForm, TractorItem } from "../machine";
import styled from "styled-components";
import { formatWonToEokMan } from "../function";
import { useFormattedNumberField } from "../LandInfo/useFormattedNumberField";
import { EditButton } from "./tractor.styles";

interface Props {
  formData: AnalysisForm;
  setFormData: Dispatch<SetStateAction<AnalysisForm>>;
}
const THUMB = 16;

function TractorSection({ formData, setFormData }: Props) {
  const [openTracktorPicker, setOpenTracktorPicker] = useState(false);
  const [fundPercent, setFundPercent] = useState(40);
  const [fixedCostPercent, setFixedCostPercent] = useState(5);
  const [maintenanceCost, setMaintenanceCost] = useState(0);
  const [insuranceCost, setInsuranceCost] = useState(0);

  const [isDetailMode, setIsDetailMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draftTractorList, setDraftTractorList] = useState<
    TractorItem[] | null
  >(null);
  const displayTractorList =
    isEditMode && draftTractorList ? draftTractorList : formData.tractorList;

  const valueLeft = useMemo(
    () => `calc(${THUMB / 2}px + (100% - ${THUMB}px) * ${fundPercent / 100})`,
    [fundPercent]
  );

  const handleChangeMode = () => {
    setFixedCostPercent(5);
    setMaintenanceCost(0);
    setInsuranceCost(0);
    setIsDetailMode((prev) => !prev);
  };

  // 연 고정비 비율
  const {
    display: fixedCostPercentDisplay,
    handleChange: handleFixedCostPercentChange,
    handleBlur: handleFixedCostPercentBlur,
  } = useFormattedNumberField({
    baseValue: fixedCostPercent,
    min: 0,
    max: 100,
    isPrice: false,
    onChangeBaseValue: (value) => {
      setFixedCostPercent(value);
    },
  });

  // 연 유지보수비
  const {
    display: maintenanceCostPercentDisplay,
    handleChange: handleMaintenanceCostPercentChange,
    handleBlur: handleMaintenanceCostPercentBlur,
  } = useFormattedNumberField({
    baseValue: maintenanceCost,
    onChangeBaseValue: (value) => {
      setMaintenanceCost(value);
    },
  });

  // 연 보험료
  const {
    display: insuranceCostPercentDisplay,
    handleChange: handleInsuranceCostPercentChange,
    handleBlur: handleInsuranceCostPercentBlur,
  } = useFormattedNumberField({
    baseValue: insuranceCost,
    onChangeBaseValue: (value) => {
      setInsuranceCost(value);
    },
  });

  const totalTractorCnt = useMemo(() => {
    return formData.tractorList.reduce((sum, item) => sum + (item.cnt ?? 1), 0);
  }, [formData.tractorList]);

  const totalTractorCost = useMemo(() => {
    return formData.tractorList.reduce(
      (sum, item) => sum + item.price * (item.cnt ?? 1),
      0
    );
  }, [formData.tractorList]);

  useEffect(() => {
    const tractorMaintenanceCost = isDetailMode
      ? (maintenanceCost + insuranceCost) * totalTractorCnt * 10_000
      : totalTractorCost * (fixedCostPercent / 100);
    setFormData((prev) => {
      return {
        ...prev,
        tractorNum: totalTractorCnt,
        tractorPurchaseCost: totalTractorCost,
        tractorMaintenanceCost,
      };
    });
  }, [
    isDetailMode,
    totalTractorCnt,
    totalTractorCost,
    fixedCostPercent,
    maintenanceCost,
    insuranceCost,
    setFormData,
  ]);

  return (
    <s.Section>
      <s.SectionTitleContainer>
        <s.SectionTitle>1. 트랙터</s.SectionTitle>
        {formData.tractorList.length > 0 && (
          <ButtonContainer>
            {isEditMode ? (
              <>
                <CancleButton
                  onClick={() => {
                    setDraftTractorList(null);
                    setIsEditMode(false);
                  }}
                >
                  취소
                </CancleButton>
                <ConfirmButton
                  onClick={() => {
                    if (draftTractorList) {
                      setFormData((prev) => ({
                        ...prev,
                        tractorList: draftTractorList,
                      }));
                    }
                    setIsEditMode(false);
                    setDraftTractorList(null);
                  }}
                >
                  완료
                </ConfirmButton>
              </>
            ) : (
              <EditButton
                onClick={() => {
                  setDraftTractorList(
                    formData.tractorList.map((t) => ({ ...t })) // 얕은 복사
                  );
                  setIsEditMode(true);
                }}
              >
                편집
              </EditButton>
            )}
          </ButtonContainer>
        )}
      </s.SectionTitleContainer>
      <s.SectionInner $top={8}>
        <t.SelectedSection>
          {displayTractorList.length > 0 && (
            <t.SelectedList>
              {displayTractorList.map((item) => {
                return (
                  <Item key={item.id}>
                    {isEditMode ? (
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setDraftTractorList((prev) =>
                            prev ? prev.filter((t) => t.id !== item.id) : prev
                          );
                        }}
                      >
                        삭제
                      </DeleteButton>
                    ) : (
                      <ItemCntNumber>{item.cnt}대</ItemCntNumber>
                    )}
                    <ItemInner>
                      <ItemTop>
                        <div className="brand">{item.brand}</div>
                        <div className="name">{item.model}</div>
                      </ItemTop>
                      <ItemContent>
                        <ContentItem>
                          <div className="label">마력</div>
                          <div className="value">{item.engine_ps}PS</div>
                        </ContentItem>
                        <ContentItem>
                          <div className="label">가격</div>
                          <div className="value">
                            {formatWonToEokMan(item.price)}
                          </div>
                        </ContentItem>
                        <ContentItem>
                          <div className="label">PTO</div>
                          <div className="value">{item.pto_ps}PS</div>
                        </ContentItem>
                        <ContentItem>
                          <div className="label">융자</div>
                          <div className="value">
                            {formatWonToEokMan(item.max_support_fund)}
                          </div>
                        </ContentItem>
                      </ItemContent>
                    </ItemInner>
                    {isEditMode && (
                      <ItemCntContainer
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <ChanceCntButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDraftTractorList((prev) =>
                              prev
                                ? prev.map((t) =>
                                    t.id === item.id
                                      ? {
                                          ...t,
                                          cnt: Math.max(1, (t.cnt ?? 1) - 1), // 최소 1대
                                        }
                                      : t
                                  )
                                : prev
                            );
                          }}
                        >
                          <img src="/asset/images/icon/minus.svg" alt="" />
                        </ChanceCntButton>
                        <ItemCnt>{item.cnt}대</ItemCnt>
                        <ChanceCntButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDraftTractorList((prev) =>
                              prev
                                ? prev.map((t) =>
                                    t.id === item.id
                                      ? {
                                          ...t,
                                          cnt: (t.cnt ?? 1) + 1,
                                        }
                                      : t
                                  )
                                : prev
                            );
                          }}
                        >
                          <img src="/asset/images/icon/plus.svg" alt="" />
                        </ChanceCntButton>
                      </ItemCntContainer>
                    )}
                  </Item>
                );
              })}
            </t.SelectedList>
          )}
          {!isEditMode && (
            <s.AddButton onClick={() => setOpenTracktorPicker(true)}>
              <img src="/asset/images/icon/plus.svg" alt="" />
              <span>보유 중인 트랙터를 추가해주세요</span>
            </s.AddButton>
          )}
        </t.SelectedSection>
      </s.SectionInner>
      <s.SectionInner $top={8}>
        <s.Label>
          <s.LabelTitle>융자지원 비율</s.LabelTitle>
        </s.Label>
        <s.SliderContainer>
          <Slider
            value={fundPercent}
            onChange={(e: any) => {
              setFundPercent(e.target.value);
            }}
            step={5}
            max={100}
            min={0}
            sx={{
              height: 8,
              padding: "15px 0",
              "& .MuiSlider-rail": {
                height: 8,
                borderRadius: 100,
                backgroundColor: "#F0F0F0",
                opacity: 1,
              },
              "& .MuiSlider-track": {
                height: 8,
                borderRadius: 100,
                backgroundColor: "#166F4D",
              },
              "& .MuiSlider-thumb": {
                width: THUMB,
                height: THUMB,
                backgroundColor: "#166F4D",
                boxShadow: "none",
                "&:hover, &.Mui-focusVisible, &.Mui-active": {
                  boxShadow: "none",
                },
              },
            }}
          />
          <s.SliderValue style={{ left: valueLeft }}>
            {fundPercent}%
          </s.SliderValue>
        </s.SliderContainer>
      </s.SectionInner>
      {isDetailMode ? (
        <>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>
                연 유지보수비
                <s.ChangeInputUnit onClick={handleChangeMode}>
                  <img src="/asset/images/change/refresh-black.svg" alt="" />
                  고정비 입력
                </s.ChangeInputUnit>
              </s.LabelTitle>
              1대당 연 유지보수비를 입력해주세요
            </s.Label>
            <s.InputWrapper>
              <s.Input
                value={maintenanceCostPercentDisplay}
                onChange={(e) =>
                  handleMaintenanceCostPercentChange(e.target.value)
                }
                onBlur={handleMaintenanceCostPercentBlur}
              />
              <span>만원/대</span>
            </s.InputWrapper>
          </s.SectionInner>
          <s.SectionInner $top={8}>
            <s.Label>
              <s.LabelTitle>연 보험료</s.LabelTitle>
              1대당 연 보험료를 입력해주세요
            </s.Label>
            <s.InputWrapper>
              <s.Input
                value={insuranceCostPercentDisplay}
                onChange={(e) =>
                  handleInsuranceCostPercentChange(e.target.value)
                }
                onBlur={handleInsuranceCostPercentBlur}
              />
              <span>만원/대</span>
            </s.InputWrapper>
          </s.SectionInner>
        </>
      ) : (
        <s.SectionInner $top={8}>
          <s.Label>
            <s.LabelTitle>
              연 고정비 비율
              <s.ChangeInputUnit onClick={handleChangeMode}>
                <img src="/asset/images/change/refresh-black.svg" alt="" />
                상세 입력
              </s.ChangeInputUnit>
            </s.LabelTitle>
            보험료, 수리비 등 고정비용의 비율 입니다
          </s.Label>
          <s.InputWrapper>
            <s.Input
              value={fixedCostPercentDisplay}
              onChange={(e) => handleFixedCostPercentChange(e.target.value)}
              onBlur={handleFixedCostPercentBlur}
            />
            <span>%</span>
          </s.InputWrapper>
        </s.SectionInner>
      )}
      <s.SectionInner $top={8}>
        <t.RowContent>
          <t.TotalContentItem>
            <div className="item-label">총 트랙터</div>
            <div className="item-value">{formData.tractorNum}대</div>
          </t.TotalContentItem>
          <t.TotalContentItem>
            <div className="item-label">총 구입비</div>
            <div className="item-value">
              {formatWonToEokMan(formData.tractorPurchaseCost)}
            </div>
          </t.TotalContentItem>
        </t.RowContent>
        <t.RowContent>
          <t.TotalContentItem>
            <div className="item-label">총 유지비</div>
            <div className="item-value">
              {formatWonToEokMan(formData.tractorMaintenanceCost)}
            </div>
          </t.TotalContentItem>
          <t.TotalContentItem>
            <div className="item-label">총 융자지원금</div>
            <div className="item-value">
              {formatWonToEokMan(
                formData.tractorPurchaseCost * (fundPercent / 100)
              )}
            </div>
          </t.TotalContentItem>
        </t.RowContent>
      </s.SectionInner>
      {openTracktorPicker && (
        <TractorPickerModal
          formData={formData}
          setFormData={setFormData}
          onClose={() => setOpenTracktorPicker(false)}
        />
      )}
    </s.Section>
  );
}

export default TractorSection;

const Item = styled.div<{ $active?: boolean }>`
  position: relative;
  cursor: pointer;
  display: flex;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-100, #f7f8f9);

  color: #171e26;
  font-size: 14px;
  font-weight: 500;

  ${({ $active }) =>
    $active &&
    `
  background: #EAF8F2;

  `}

  .brand {
    color: rgba(23, 30, 38, 0.78);
  }

  .name {
    font-size: 16px;
  }

  .label {
    width: 32px;
    color: rgba(23, 30, 38, 0.56);
  }

  .value {
    font-weight: 700;
  }
`;

const ItemInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const ItemTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`;

const ItemContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 0px;
  align-self: stretch;
`;

const ContentItem = styled.div`
  display: flex;
  padding: 4px 0;
  align-items: flex-start;
  align-self: stretch;
`;

const ItemCntNumber = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;

  color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);

  /* Mo/Body/L-16-Bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CancleButton = styled(EditButton)`
  border: 1px solid var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
  background: var(--Alias-Color-Text-White-Gray-White, #fff);
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
`;

const ConfirmButton = styled(EditButton)`
  background: var(--Alias-Color-Button-cta-light-background-Primary, #1e9b6b);
  color: var(--Alias-Color-Text-White-Text-Primary, #fff);
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;

  border: none;
  outline: none;
  background-color: transparent;
  cursor: pointer;

  color: var(--Alias-Color-Text-White-States-Error, #d84836);
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ItemCntContainer = styled.div`
  display: flex;
  padding: 4px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-White, #fff);
`;

const ChanceCntButton = styled.button`
  border: none;
  cursor: pointer;

  display: flex;
  width: 40px;
  height: 40px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);
`;
const ItemCnt = styled.div`
  display: flex;
  padding: 8px;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;

  /* Mo/Body/L-16-Medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;
